
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOptimizedAuth } from "@/hooks/useOptimizedAuth";
import { useOptimizedCart } from "@/contexts/OptimizedCartContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { 
  Menu, 
  Home, 
  ShoppingCart, 
  Heart, 
  User, 
  Settings,
  Sun,
  Moon,
  Monitor,
  LogOut,
  Package,
  Shield,
  Receipt
} from "lucide-react";

const ComprehensiveMenu = () => {
  const { user, signOut, userRole } = useOptimizedAuth();
  const { itemCount } = useOptimizedCart();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleWishlistClick = () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    navigate('/wishlist');
  };

  const handleOrdersClick = () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    navigate('/orders');
  };

  const getThemeIcon = () => {
    switch (theme) {
      case 'light': return <Sun className="h-4 w-4" />;
      case 'dark': return <Moon className="h-4 w-4" />;
      case 'system': return <Monitor className="h-4 w-4" />;
      default: return <Monitor className="h-4 w-4" />;
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-foreground"
        >
          <Menu className="h-6 w-6" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {/* Navigation Section */}
        <DropdownMenuLabel>Navigation</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => handleNavigation('/')}>
          <Home className="mr-2 h-4 w-4" />
          Home
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleNavigation('/products')}>
          <Package className="mr-2 h-4 w-4" />
          Products
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleNavigation('/categories')}>
          <Settings className="mr-2 h-4 w-4" />
          Categories
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Shopping Section */}
        <DropdownMenuLabel>Shopping</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => handleNavigation('/cart')}>
          <ShoppingCart className="mr-2 h-4 w-4" />
          Cart
          {itemCount > 0 && (
            <Badge variant="secondary" className="ml-auto">
              {itemCount}
            </Badge>
          )}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleWishlistClick}>
          <Heart className="mr-2 h-4 w-4" />
          Wishlist
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleOrdersClick}>
          <Receipt className="mr-2 h-4 w-4" />
          Orders
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Theme Section */}
        <DropdownMenuLabel>Appearance</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => setTheme('light')}>
          <Sun className="mr-2 h-4 w-4" />
          Light
          {theme === 'light' && <span className="ml-auto">✓</span>}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme('dark')}>
          <Moon className="mr-2 h-4 w-4" />
          Dark
          {theme === 'dark' && <span className="ml-auto">✓</span>}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme('system')}>
          <Monitor className="mr-2 h-4 w-4" />
          System
          {theme === 'system' && <span className="ml-auto">✓</span>}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* User Section */}
        {user ? (
          <>
            <DropdownMenuLabel>Account</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => handleNavigation('/settings')}>
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>
            {userRole === 'admin' && (
              <DropdownMenuItem onClick={() => handleNavigation('/admin')}>
                <Shield className="mr-2 h-4 w-4" />
                Admin
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuLabel>Account</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => handleNavigation('/auth')}>
              <User className="mr-2 h-4 w-4" />
              Sign In
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ComprehensiveMenu;
